<template>
  <section :class="sectionClass" v-intersection-observer="onVisible">
    <slot name="background"></slot>
    <v-container>
      <div class="section-content">
        <h2 v-if="title" class="section-title">{{ title }}</h2>
        <p v-if="subtitle" class="section-subtitle">{{ subtitle }}</p>
        <slot></slot>
      </div>
    </v-container>
  </section>
</template>

<script setup lang="ts">
import { getCurrentInstance, type DirectiveBinding } from 'vue'

defineProps<{
  title?: string
  subtitle?: string
  sectionClass?: string
}>()

const emit = defineEmits<{
  visible: [visible: boolean]
}>()

const vIntersect = {
  mounted(el: Element, binding: DirectiveBinding) {
    const options = { root: null, rootMargin: '0px', threshold: 0.15 }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          try {
            if (typeof binding.value === 'function') binding.value(true)
          } catch (e) {
            console.error('Intersection handler error', e)
          }
        }
      })
    }, options)
    observer.observe(el)
    ;(el as HTMLElement & { __v_intersection_observer?: IntersectionObserver }).__v_intersection_observer = observer
  },
  unmounted(el: Element) {
    const observer = (el as HTMLElement & { __v_intersection_observer?: IntersectionObserver }).__v_intersection_observer
    if (observer && typeof observer.disconnect === 'function') observer.disconnect()
  }
}

const instance = getCurrentInstance()
if (instance && instance.appContext && instance.appContext.app) {
  instance.appContext.app.directive('intersection-observer', vIntersect)
}

const onVisible = (visible: boolean) => {
  if (visible) emit('visible', true)
}
</script>

<style scoped>
.section-content {
  text-align: center;
  position: relative;
  z-index: var(--z-content);
}

.section-title {
  font-size: clamp(1.5rem, 4vw + 0.5rem, 3rem);
  font-weight: 700;
  line-height: 1.1;
  margin-bottom: 16px;
  color: var(--text-primary);
  font-family: var(--font-family-body);
}

.section-subtitle {
  font-size: clamp(0.9rem, 2vw + 0.25rem, 1.25rem);
  color: var(--text-body);
  margin-bottom: 48px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 1rem;
  font-family: var(--font-family-body);
}

@media (max-width: 767px) {
  .section-title {
    font-size: var(--font-size-5xl);
    margin-bottom: 12px;
  }

  .section-subtitle {
    font-size: var(--font-size-lg);
    margin-bottom: 32px;
  }
}

@media (max-width: 639px) {
  .section-title {
    font-size: var(--font-size-3xl);
  }

  .section-subtitle {
    font-size: var(--font-size-md);
  }
}
</style>
